import * as React from 'react';

import { useCan } from '@/components/can/Can';
import CrumbTitle from '@/components/crumbTitle/CrumbTitle';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDynamicLocale } from '@/hoslog-intl';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { DiscountCategory } from '@/services/discount-management/discount-category.types';
import { useUpdateDiscountCategoryStatus } from '../../hooks/useManageDiscountCategory';
import ManageDiscountFormContainer from '../../ManageDiscountFormContainer';
import { getColumns } from './Column';

export function DiscountCategoryTable({
  data,
  isLoading = false,
  pagination,
}: {
  data: DiscountCategory[];
  isLoading: boolean;
  pagination: Pagination;
}) {
  const { t } = useDynamicLocale();
  const { onOpen } = useDialogFormStore();
  const { openConfirmation } = useConfirmationStore();
  const { mutateAsync } = useUpdateDiscountCategoryStatus();
  const canAddNew = useCan({ allOf: [PERMS.discount.category.manage] });

  const columns = React.useMemo(
    () => getColumns(onOpen, t, mutateAsync, openConfirmation),
    [onOpen, openConfirmation, t, mutateAsync],
  );
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const onAddNew = () => {
    onOpen({
      title: 'Add New Commission Slab',
      okText: <CrumbTitle id="common.page.modal.submit" />,
      size: 'lg',
      modalTitle: 'configurationManagement.discountManagement.category.manageDiscountCategory.title',
      component: <ManageDiscountFormContainer />,
    });
  };

  const { table } = useDataTable({
    data,
    columns,
    pageCount: pageCount,
    initialState: {
      sorting: [{ id: 'categoryId', desc: true }],
    },
    meta: {
      title: t('configurationManagement.discountManagement.category.table.title'),
      actionKey: canAddNew ? 'onAddNew' : '',
      action: canAddNew ? onAddNew : null,
      loading: isLoading,
      totalPage: pagination.totalItems,
    },
    getRowId: (originalRow) => String(originalRow.categoryId),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table}>
        <DataTableToolbar />
      </DataTable>
    </>
  );
}
